import { GetStaticPaths, GetStaticProps } from 'next';
import { getLanguagesPaths } from '~/utils/translate';
import { useRouter } from 'next/router';
import { Translate, useTranslate, NextLink } from '~/i18n';
import { CloseIconLight, AngelLeftIcon, AngelRightIcon } from '~/svgs/common';
import { RedVoucher } from '~/svgs/vouchers';
import Container from '~/components/common/container';

/**
 * This page contains the guide on how vouchers work
 */
const ExplainVouchers = () => {
   const { dir } = useTranslate();
   const router = useRouter();

   const goBack = () => {
      router.back();
   };

   return (
      <Container className="flex flex-col tablet:grid tablet:grid-cols-12 tablet:gap-x-16 text-white">
         <style jsx global>
            {`
               body #__next main {
                  background-color: black;
               }
            `}
         </style>
         <CloseIconLight
            className="tablet:hidden hover:opacity-60 focus:opacity-60 absolute ltr:right-0 rtl:left-0 m-4 z-70 cursor-pointer border rounded-full border-primary-disabled p-px"
            onClick={goBack}
         />
         <div className="col-span-7">
            <button
               onClick={goBack}
               className="hidden tablet:flex items-center mt-14 text-sm max-w-max hover:opacity-60">
               {dir === 'ltr' ? <AngelLeftIcon /> : <AngelRightIcon />}
               <div>
                  <Translate id="vouchers:back to vouchers" />
               </div>
            </button>
            <div className="mt-20 tablet:mt-16 mb-4 tablet:mb-20 text-center tablet:ltr:text-left tablet:rtl:text-right">
               <h1 className="bg-clip-text bg-gradient-to-br from-indigo-200 to-red-300 font-black mb-6 text-3xl text-transparent">
                  <Translate id="vouchers:voucher any experience" />
               </h1>
               <p>
                  <Translate id="vouchers:can buy and redeem" />
               </p>
               <div className="tablet:hidden">
                  <img src="/images/vouchers/how-vouchers-work-1.png" />
               </div>
            </div>
            <div className="border-b border-gray-deminished tablet:border-none mb-4">
               <h2 className="text-2xl font-bold mb-3">
                  <Translate id="vouchers:buy and share" />
               </h2>
               <p className="mb-5">
                  <Translate id="vouchers:from where" />
               </p>
               <div className="mb-9">
                  {dir === 'ltr' ? (
                     <img src="/images/vouchers/how-vouchers-work-2.png" />
                  ) : (
                     <img src="/images/vouchers/how-vouchers-work-2-ar.png" />
                  )}
               </div>
               <p className="mb-8">
                  <Translate id="vouchers:added but not redeemed until" />
               </p>
               <div className="text-xs small:text-base mb-2">
                  <Translate id="vouchers:active" />
               </div>
               <div className="mb-6">
                  {dir === 'ltr' ? (
                     <img src="/images/vouchers/how-vouchers-work-3.png" />
                  ) : (
                     <img src="/images/vouchers/how-vouchers-work-3-ar.png" />
                  )}
               </div>
               <p className="mb-7">
                  <Translate id="vouchers:share with friend or family" />
               </p>
               <div className="mb-9">
                  {dir === 'ltr' ? (
                     <img src="/images/vouchers/how-vouchers-work-4.png" />
                  ) : (
                     <img src="/images/vouchers/how-vouchers-work-4-ar.png" />
                  )}
               </div>
            </div>
            <div className="border-b border-gray-deminished tablet:border-none tablet:mb-32">
               <h2 className="text-2xl font-bold mb-3">
                  <Translate id="vouchers:redeem voucher" />
               </h2>
               <p className="mb-2">
                  <Translate id="vouchers:to redeem" />
               </p>
               <p className="mb-3">
                  <Translate id="vouchers:if friend shared" />
               </p>
               <div className="mb-4">
                  {dir === 'ltr' ? (
                     <img src="/images/vouchers/how-vouchers-work-5.png" />
                  ) : (
                     <img src="/images/vouchers/how-vouchers-work-5-ar.png" />
                  )}
               </div>
            </div>
         </div>
         <div className="col-span-5">
            <NextLink
               href="/profile/vouchers"
               className="tablet:hidden bg-opacity-20 bg-white flex my-7 py-3 px-4 rounded-default rtl:space-x-reverse space-x-4">
               <div>
                  <RedVoucher />
               </div>
               <div className="flex-grow">
                  <Translate id="vouchers:my vouchers" />
               </div>
               <div>{dir === 'ltr' ? <AngelRightIcon /> : <AngelLeftIcon />}</div>
            </NextLink>
            <div className="hidden tablet:block mt-28">
               <img src="/images/vouchers/how-vouchers-work-1.png" />
            </div>
            <div className="hidden tablet:block mt-16 divide-y divide-black mx-7">
               {/* add focus and hover effects */}
               <NextLink
                  href="/profile/vouchers"
                  className="flex py-3 px-3.5 bg-opacity-20 bg-white hover:bg-opacity-30 rounded-t-default">
                  <div className="flex-grow">
                     <Translate id="vouchers:redeem a voucher" />
                  </div>
                  <div>{dir === 'ltr' ? <AngelRightIcon /> : <AngelLeftIcon />}</div>
               </NextLink>
               <NextLink
                  href="/profile/vouchers"
                  className="flex py-3 px-3.5 bg-opacity-20 bg-white hover:bg-opacity-30">
                  <div className="flex-grow">
                     <Translate id="vouchers:my vouchers" />
                  </div>
                  <div>{dir === 'ltr' ? <AngelRightIcon /> : <AngelLeftIcon />}</div>
               </NextLink>
               <NextLink
                  href="/vouchers/voucher-terms"
                  className="flex py-3 px-3.5 bg-opacity-20 bg-white hover:bg-opacity-30 rounded-b-default">
                  <div className="flex-grow">
                     <Translate id="common:terms and conditions" />
                  </div>
                  <div>{dir === 'ltr' ? <AngelRightIcon /> : <AngelLeftIcon />}</div>
               </NextLink>
            </div>
         </div>
      </Container>
   );
};

export default ExplainVouchers;

export const getStaticPaths: GetStaticPaths = async () => {
   return {
      paths: getLanguagesPaths(),
      fallback: false,
   };
};

/******************************************************************
 *
 *  THIS PAGE IS USING STATIC SITE GENERATION
 *
 ******************************************************************/

export const getStaticProps: GetStaticProps = async () => {
   return {
      props: {},
   };
};
